export type LinkReasonCode =
  | 'LINK_NO_SEGMENT'
  | 'LINK_SEGMENT_EMPTY'
  | 'LINK_AGENT_ONLY_STEP'
  | 'LINK_NO_MUTATION_FOR_STEP'
  | 'LINK_VALUE_TOO_WEAK'
  | 'LINK_VALUE_NOT_IN_TRACE'
  | 'LINK_AMBIGUOUS_VALUE'
  | 'LINK_ROW_SELECTOR_UNRESOLVED'
  | 'LINK_AMBIGUOUS_CANDIDATES'
  | 'LINK_TOO_MANY_CANDIDATES'
  | 'LINK_RUNCODE_NONLINEAR'
  | 'LINK_RUNCODE_PARTIAL_ALIGNMENT'
  | 'LINK_RUNCODE_INTERLEAVED'
  | 'LINK_AMBIGUOUS_SEQUENCE'
  | 'LINK_MONOTONICITY_VIOLATION'
  | 'LINK_LAYER_CONFLICT'
  | 'LINK_TARGET_ERRORED';

export type GroundTruthReasonCode =
  | 'GT_UNCONFIRMED_SINGLE_SIGNAL'
  | 'GT_SIGNAL_CONFLICT'
  | 'GT_NO_BEFORE_SNAPSHOT'
  | 'GT_MARKER_AMBIGUOUS'
  | 'GT_POINT_OFFVIEWPORT'
  | 'GT_IFRAME_UNSUPPORTED'
  | 'GT_FILLFORM_FIELD_UNVERIFIED'
  | 'AUTHOR_IRREDUCIBLE'
  | 'AUTHOR_VOLATILE_TOKENS'
  | 'VERIFY_FAILED_UNIQUENESS'
  | 'VERIFY_FAILED_TARGET_MISMATCH'
  | 'VERIFY_FAILED_VOLATILE';

export type ComparatorReasonCode =
  | 'CMP_NOT_BETTER'
  | 'CMP_INCUMBENT_STREAK_PROTECTED'
  | 'CMP_BATTERY_REGRESSION'
  | 'CMP_NO_INCUMBENT';

export type StepDecisionReasonCode = LinkReasonCode | GroundTruthReasonCode | ComparatorReasonCode;

/**
 * Decision `signals` are non-reason breadcrumbs: they explain which hard proof
 * path was used without changing the outcome taxonomy. Group-scope signals:
 *
 * - `group_scope:playbook-step` — producer `Playbook Step <order>:` groups were
 *   well-formed for the node and candidate sets were narrowed by group ancestry.
 * - `group_scope:malformed` — a producer playbook-step group was present but not
 *   provably well-formed, so all such groups for the node were ignored.
 * - `group_scope:run-code-executed` — a well-formed `Run Code <seq>:` group was
 *   joined to a recorded browser_run_code/browser_evaluate call and the executed
 *   trace actions in that group scoped Layer 3.
 * - `group_scope:join-mismatch` — run-code groups were present, but their seqs
 *   could not be joined 1:1 to recorded browser code calls, so they were ignored.
 */
export type DecisionSignalCode =
  | 'group_scope:playbook-step'
  | 'group_scope:malformed'
  | 'group_scope:run-code-executed'
  | 'group_scope:join-mismatch';

export type ShadowCandidate =
  | {
      success: {
        predicate: string;
        selectorAuthor: 'deterministic';
        verified: true;
        markerTag: string;
        markerText?: string;
        layer: 1 | 2 | 3 | 4;
      };
    }
  | {
      failure: {
        failedReason: GroundTruthReasonCode;
        detail: string;
      };
    };

export interface StepDecision {
  stepOrder: number;
  outcome: 'improved' | 'incumbent_kept' | 'carry_through' | 'pending' | 'gap';
  layer?: 1 | 2 | 3 | 4;
  reason?: StepDecisionReasonCode;
  detail?: string;
  signals?: string[];
  candidateActionIndices?: number[];
  shadowCandidate?: ShadowCandidate;
  timingsMs?: Record<string, number>;
}

export interface NodeDecisionLog {
  nodeId: string;
  decisions: StepDecision[];
  linkStats: {
    byLayer: Record<string, number>;
    gapsByReason: Record<string, number>;
  };
}

function sortForJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => sortForJson(entry));
  }

  if (value === null || typeof value !== 'object') {
    return value;
  }

  const record = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(record).sort()) {
    const child = record[key];
    if (child !== undefined) out[key] = sortForJson(child);
  }
  return out;
}

export function serializeNodeDecisionLog(log: NodeDecisionLog): string {
  return JSON.stringify(sortForJson(log), null, 2);
}
